/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  EventType,
  ListSpendRuleEventsRequest,
  ListSpendRuleEventsSecurity,
} from "../models/operations/listspendruleevents.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildSpendRulesListEventsQuery,
  prefetchSpendRulesListEvents,
  queryKeySpendRulesListEvents,
  SpendRulesListEventsQueryData,
} from "./spendRulesListEvents.core.js";
export {
  buildSpendRulesListEventsQuery,
  prefetchSpendRulesListEvents,
  queryKeySpendRulesListEvents,
  type SpendRulesListEventsQueryData,
};

export type SpendRulesListEventsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listSpendRuleEvents spendRules
 *
 * @remarks
 * List warning and breach events emitted by budget rule evaluation, most recent first.
 */
export function useSpendRulesListEvents(
  request?: ListSpendRuleEventsRequest | undefined,
  security?: ListSpendRuleEventsSecurity | undefined,
  options?: QueryHookOptions<
    SpendRulesListEventsQueryData,
    SpendRulesListEventsQueryError
  >,
): UseQueryResult<
  SpendRulesListEventsQueryData,
  SpendRulesListEventsQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildSpendRulesListEventsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listSpendRuleEvents spendRules
 *
 * @remarks
 * List warning and breach events emitted by budget rule evaluation, most recent first.
 */
export function useSpendRulesListEventsSuspense(
  request?: ListSpendRuleEventsRequest | undefined,
  security?: ListSpendRuleEventsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    SpendRulesListEventsQueryData,
    SpendRulesListEventsQueryError
  >,
): UseSuspenseQueryResult<
  SpendRulesListEventsQueryData,
  SpendRulesListEventsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildSpendRulesListEventsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setSpendRulesListEventsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      ruleId?: string | undefined;
      eventType?: EventType | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: SpendRulesListEventsQueryData,
): SpendRulesListEventsQueryData | undefined {
  const key = queryKeySpendRulesListEvents(...queryKeyBase);

  return client.setQueryData<SpendRulesListEventsQueryData>(key, data);
}

export function invalidateSpendRulesListEvents(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      ruleId?: string | undefined;
      eventType?: EventType | undefined;
      cursor?: string | undefined;
      limit?: number | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "events", "list", ...queryKeyBase],
  });
}

export function invalidateAllSpendRulesListEvents(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "events", "list"],
  });
}
